import { Navigate, Outlet, Route } from 'react-router-dom';

import { ResponsiveLayout } from '@/components/widgets/t/ResponsiveLayout/ResponsiveLayout.layout';
import { RestrictedAccessPage } from '@/pages/restricted-access/RestrictedAccess.page';
import { AccountEditEmailPage } from '@/pages/shared/account/pages/edit-email/EditEmail.page';
import { AccountEditNamePage } from '@/pages/shared/account/pages/edit-name/EditName.page';
import { AccountEditPasswordPage } from '@/pages/shared/account/pages/edit-password/EditPassword.page';
import { AccountPage } from '@/pages/shared/account/pages/index/Account.page';
import { AccountLegalPage } from '@/pages/shared/account/pages/legal/AccountLegal.page';
import { AccountTeamPage } from '@/pages/shared/account/pages/team/AccountTeam.page';
import { AccountTeamEditMemberPage } from '@/pages/shared/account/pages/team/pages/edit-member/AccountTeamEditMember.page';
import { AccountTeamInvitePage } from '@/pages/shared/account/pages/team/pages/invite/AccountTeamInvite.page';
import { ComparePage } from '@/pages/shared/compare/Compare.page';
import { CompareListPage } from '@/pages/shared/compare/CompareList.page';
import { ComplianceFrameworksPage } from '@/pages/shared/compliance-frameworks/ComplianceFrameworks.page';
import { ComplianceFrameworksPageWrapper } from '@/pages/shared/compliance-frameworks/components/ComplianceFrameworks.page-wrapper';
import { CopilotPage } from '@/pages/shared/copilot/Copilot.page';
import { DashboardPage } from '@/pages/shared/dashboard/Dashboard.page';
import { GoalsPage } from '@/pages/shared/goals/Goals.page';
import { RoadmapPage } from '@/pages/shared/goals/pages/Roadmap.page';
import { IndicatorsPage } from '@/pages/shared/indicators/Indicators.page';
import { KnowledgePage } from '@/pages/shared/knowledge/Knowledge.page';
import { LandBiodiversityPage } from '@/pages/shared/land/LandBiodiversity.page';
import { LandBiodiversityWithDeforestation } from '@/pages/shared/land/LandBiodiversityWithDeforestation.page';
import { LandCarbonPage } from '@/pages/shared/land/LandCarbon.page';
import { LandSocialPage } from '@/pages/shared/land/LandSocial.page';
import { LandSoilPage } from '@/pages/shared/land/LandSoil.page';
import { LandWaterPage } from '@/pages/shared/land/LandWater.page';
import { MediaUploadPage } from '@/pages/shared/media-upload/MediaUpload.page';
import { PlotOverviewPage } from '@/pages/shared/media-upload/pages/PlotOverview.page';
import { OrganizationOnboardingPage } from '@/pages/shared/organization/pages/Onboarding.page';
import { InteractiveMapPage } from '@/pages/shared/plots/pages/InteractiveMap.page';
import { PlotGroupsPage } from '@/pages/shared/plots/PlotGroups.page';
import { PlotsPage } from '@/pages/shared/plots/Plots.page';
import { ContractsPage } from '@/pages/shared/portfolio/pages/contracts/Contracts.page';
import { PortfolioNaturalCapitalPage } from '@/pages/shared/portfolio/pages/NaturalCapital/PortfolioNaturalCapital.page';
import { StocksPage } from '@/pages/shared/portfolio/pages/stocks/Stocks.page';
import { UpliftUnitsPage } from '@/pages/shared/portfolio/pages/uplift/UpliftUnits.page';
import { PortfolioForecastsPage } from '@/pages/shared/portfolio/PortfolioForecasts.page';
import { PortfolioInvestmentsPage } from '@/pages/shared/portfolio/PortfolioInvestments.page';
import { DetailsPage } from '@/pages/shared/projects/pages/project/Details.page';
import { NDVIDetailPage } from '@/pages/shared/projects/pages/project/pages/conservation/restoration/pages/ndvi/NDVIDetail.page';
import { InteractiveMap } from '@/pages/shared/projects/pages/project/pages/conservation/shared/pages/interactive-map/interactive-map.page';
import { DeforestationDetailPage } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/deforestation/DeforestationDetail.page';
import { ForestStructureDetailPage } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/forestStructure/ForestStructureDetail.page';
import { DefaultPlotViewWithDeforestation } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/DefaultPlotViewWithDeforestation';
import { ProjectPage } from '@/pages/shared/projects/pages/project/Project.page';
import { ProjectsPage } from '@/pages/shared/projects/Projects.page';
import { ReportsPage } from '@/pages/shared/reports/Reports.page';
import {
  EcologicalIntegrityPages,
  HabitatIntactnessPages,
  IndicatorSpeciesPages,
} from '@/routing/components/ConservationPages';
import { WaterDetailPages } from '@/routing/components/ProjectTypePages';
import { HomePageProtection } from '@/routing/components/protections/home-page/HomePageProtection';
import {
  RequireAccessiblePlot,
  RequireAccessiblePlotWithoutProject,
} from '@/routing/components/protections/require-accessible-plot/RequireAccessiblePlot';
import { RequireAccessibleProject } from '@/routing/components/protections/require-accessible-project/RequireAccessibleProject';
import { RequireAdminRole } from '@/routing/components/protections/require-admin-role/RequireAdminRole';
import { ConservationProtection } from '@/routing/components/protections/require-conservation/ConservationProtection';
import { NoGroupByProtection } from '@/routing/components/protections/require-group-by/NoGroupByProtection';
import { RequireSupportPlot } from '@/routing/components/protections/require-support-plot/RequireSupportPlot';
import { SkipLoggedInUser } from '@/routing/components/protections/skip-logged-in-user/SkipLoggedInUser';
import { paths } from '@/routing/routing';

import { AccountPageWrapper } from '../../shared/account/components/Account.page-wrapper';
import { PlotPage } from '../../shared/projects/pages/project/pages/sustainableProduction/pages/plot/Plot.page';
import { LandStewardCompleteAccountPage } from '../pages/complete-account/complete-account.page';
import { LandstewardInvestmentsPage } from '../pages/investments/Investments.page';
import { LandstewardEditPlotPage } from '../pages/plot/pages/edit/edit-plot.page';
import { LandstewardNewPlotPage } from '../pages/plot/pages/new/new-plot.page';
import { LandstewardPortfolioPage } from '../pages/portfolio/Portfolio.page';
import { ProjectsNewPage } from '../pages/projects/pages/new-project/NewProject.page';
import { LandStewardSignUpPage } from '../pages/sign-up/sign-up.page';
import { LandStewardSignedInRoute } from './components/protections/land-steward-signed-in-route/LandStewardSignedInRoute';

export const LandStewardPageRoutes = (
  <Route path='landsteward'>
    <Route element={<LandStewardSignedInRoute />}>
      <Route element={<ResponsiveLayout />}>
        <Route path={paths.landSteward.account} element={<AccountPageWrapper />}>
          <Route index element={<AccountPage />} />
          <Route path={paths.landSteward.accountEditEmail} element={<AccountEditEmailPage />} />
          <Route path={paths.landSteward.accountEditName} element={<AccountEditNamePage />} />
          <Route path={paths.landSteward.accountEditPassword} element={<AccountEditPasswordPage />} />
          <Route path={paths.landSteward.accountLegal} element={<AccountLegalPage />} />
          <Route path={paths.landSteward.accountTeam}>
            <Route index element={<AccountTeamPage />} />
            <Route element={<RequireAdminRole />}>
              <Route path={paths.landSteward.accountTeamEditMember} element={<AccountTeamEditMemberPage />} />
              <Route path={paths.landSteward.accountTeamInvite} element={<AccountTeamInvitePage />} />
            </Route>
          </Route>
        </Route>

        <Route path={paths.landSteward.socialDetails} element={<LandSocialPage />} />

        <Route path={paths.landSteward.portfolio} element={<LandstewardPortfolioPage />} />

        <Route path={paths.landSteward.portfolioNaturalCapital} element={<PortfolioNaturalCapitalPage />} />
        <Route path={paths.landSteward.portfolioForecasts} element={<PortfolioForecastsPage />} />
        <Route path={paths.landSteward.portfolioInvestments} element={<PortfolioInvestmentsPage />} />
        <Route path={paths.landSteward.portfolioUnits} element={<UpliftUnitsPage />} />
        <Route path={paths.landSteward.portfolioStocks} element={<StocksPage />} />
        <Route path={paths.landSteward.portfolioContracts} element={<ContractsPage />} />

        <Route path={paths.landSteward.investments} element={<LandstewardInvestmentsPage />} />
        <Route element={<RequireSupportPlot />}>
          <Route path={paths.landSteward.plots} element={<PlotsPage />} />
          <Route element={<NoGroupByProtection />}>
            <Route path={paths.landSteward.groups} element={<PlotGroupsPage />} />
            <Route path={paths.landSteward.comparisons} element={<CompareListPage />} />
            <Route path={paths.landSteward.compare} element={<ComparePage />} />
          </Route>
        </Route>
        <Route index element={<Navigate to={paths.landSteward.projects} replace />} />

        <Route element={<NoGroupByProtection />}>
          <Route path={`${paths.landSteward.details}/*`} element={<DetailsPage />} />
          <Route path={paths.landSteward.biodiversityDetails} element={<LandBiodiversityPage />} />
          <Route
            path={paths.landSteward.biodiversityWithDeforestationDetails}
            element={<LandBiodiversityWithDeforestation />}
          />
          <Route path={paths.landSteward.soilDetails} element={<LandSoilPage />} />
          <Route path={paths.landSteward.carbonDetails} element={<LandCarbonPage />} />
          <Route path={paths.landSteward.waterDetails} element={<LandWaterPage />} />
          <Route path={paths.landSteward.deforestationDetails} element={<DeforestationDetailPage />} />
          <Route path={paths.landSteward.forestDetails} element={<ForestStructureDetailPage />} />
        </Route>

        <Route path={paths.landSteward.projects}>
          <Route path={paths.landSteward.newProject} element={<ProjectsNewPage />} />
          <Route index element={<ProjectsPage />} />
          <Route path={paths.landSteward.projectsComplianceFrameworks} element={<ComplianceFrameworksPageWrapper />}>
            <Route index element={<ComplianceFrameworksPage />} />
          </Route>
          <Route element={<RequireAccessibleProject />}>
            <Route path={paths.landSteward.ecologicalIntegrity} element={<EcologicalIntegrityPages />} />
            <Route path={paths.landSteward.habitatIntactness} element={<HabitatIntactnessPages />} />
            <Route path={paths.landSteward.indicatorSpecies} element={<IndicatorSpeciesPages />} />
            <Route path={paths.landSteward.ndvi} element={<NDVIDetailPage />} />
            <Route path={`${paths.landSteward.projectDetails}/*`} element={<ProjectPage />} />
            <Route path={paths.landSteward.interactiveMap} element={<InteractiveMap />} />
            <Route path={paths.landSteward.projectDetailsWater} element={<WaterDetailPages />} />

            <Route element={<RequireAccessiblePlot />}>
              <Route path={`${paths.landSteward.plot}/*`} element={<PlotPage />} />
              <Route path={paths.landSteward.plotWithDeforestation} element={<DefaultPlotViewWithDeforestation />} />
            </Route>
            <Route path={`${paths.landSteward.editPlot}/*`} element={<LandstewardEditPlotPage />} />
            <Route path={`${paths.landSteward.newPlot}/*`} element={<LandstewardNewPlotPage />} />
          </Route>
        </Route>
        <Route element={<RequireAccessiblePlotWithoutProject />}>
          <Route path={paths.landSteward.plotInteractiveMap} element={<InteractiveMapPage />} />
        </Route>

        <Route path={paths.landSteward.restrictedAccess} element={<RestrictedAccessPage />} />
        <Route path={paths.landSteward.indicators} element={<IndicatorsPage />} />
        <Route element={<HomePageProtection />}>
          <Route path={paths.landSteward.dashboard} element={<DashboardPage />} />
        </Route>

        <Route path={paths.landSteward.contracts} element={<ContractsPage />} />
        <Route path={paths.landSteward.reports} element={<ReportsPage />} />

        <Route path={paths.landSteward.goals} element={<GoalsPage />} />
        <Route path={paths.landSteward.roadmap} element={<RoadmapPage />} />

        <Route path={paths.landSteward.copilot} element={<CopilotPage />} />
        <Route path={paths.landSteward.knowledge} element={<KnowledgePage />} />
        <Route element={<ConservationProtection />}>
          <Route path={paths.landSteward.mediaUpload} element={<MediaUploadPage />} />
          <Route path={paths.landSteward.mediaUploadPlotOverview} element={<PlotOverviewPage />} />
        </Route>
      </Route>
      <Route path={paths.landSteward.organizationOnboarding} element={<OrganizationOnboardingPage />} />
    </Route>
    <Route
      element={
        <SkipLoggedInUser>
          <Outlet />
        </SkipLoggedInUser>
      }
    >
      <Route path={paths.landSteward.completeAccount} element={<LandStewardCompleteAccountPage />} />
      <Route path={paths.landSteward.signUp} element={<LandStewardSignUpPage />} />
    </Route>
  </Route>
);
